"use client"

import { useEffect, useState, useRef } from "react"
import { Card, Button, Table, Badge, Form, Dropdown, Modal, Container } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchAllTickets,
  fetchTicketCounts,
  updateTicketStatus,
  deleteTicket,
  clearTickets,
} from "../../../redux/slices/ticketsSlice"
import { fetchBuildings } from "../../../redux/slices/buildingsSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Filter, XCircle, Trash2 } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { formatDate } from "../../../utils/timestamp"
import "../../../styles/admin-buttons.css"
import { fetchSoftoneRequests, sendSoftoneRequest } from "../../../api/softone"
import type { SoftoneRequestRecord } from "../../../data/softone"
import { SoftoneRequestBadge } from "../../../components/admin/softone/SoftoneRequestBadge"

export default function AdminTicketsPage() {
  const dispatch = useAppDispatch()
  const { tickets, ticketCounts, isLoading } = useAppSelector((state) => state.tickets)
  const { buildings } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState(() => searchParams.get("status") || "open")
  const [buildingFilter, setBuildingFilter] = useState(() => searchParams.get("building") || "all")
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "")
  const mainAbortControllerRef = useRef<AbortController | null>(null)
  const unfilteredAbortControllerRef = useRef<AbortController | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; ticketId: string; ticketTitle: string }>({
    show: false,
    ticketId: "",
    ticketTitle: "",
  })
  const location = useLocation()

  const navigate = useNavigate()

  const updateFilters = (newFilter: string, newBuildingFilter: string, newSearchTerm: string) => {
    const params = new URLSearchParams()
    if (newFilter !== "open") params.set("status", newFilter)
    if (newBuildingFilter !== "all") params.set("building", newBuildingFilter)
    if (newSearchTerm) params.set("search", newSearchTerm)
    setSearchParams(params)
  }

  // Buildings and counts don't depend on the status / building filter: load them once per visit
  // (status changes and deletes below refresh the counts)
  useEffect(() => {
    dispatch(fetchBuildings(false))

    if (unfilteredAbortControllerRef.current) {
      unfilteredAbortControllerRef.current.abort()
    }
    unfilteredAbortControllerRef.current = new AbortController()
    dispatch(fetchTicketCounts({ signal: unfilteredAbortControllerRef.current.signal }))
  }, [dispatch])

  useEffect(() => {
    if (mainAbortControllerRef.current) {
      mainAbortControllerRef.current.abort()
    }

    dispatch(clearTickets())

    mainAbortControllerRef.current = new AbortController()

    dispatch(
      fetchAllTickets({
        status: filter === "all" ? undefined : filter,
        building: buildingFilter === "all" ? undefined : buildingFilter,
        signal: mainAbortControllerRef.current.signal,
      }),
    )
  }, [dispatch, filter, buildingFilter])

  useEffect(() => {
    return () => {
      if (mainAbortControllerRef.current) {
        mainAbortControllerRef.current.abort()
      }
      if (unfilteredAbortControllerRef.current) {
        unfilteredAbortControllerRef.current.abort()
      }
    }
  }, [])

  const handleUpdateStatus = async (ticketId: string, status: "open" | "closed") => {
    try {
      const result = await dispatch(updateTicketStatus({ ticketId, status })).unwrap()
      if (result.success) {
        toast({
          title: "Success",
          description: `Ticket ${status === "closed" ? "closed" : "reopened"} successfully`,
        })

        if (unfilteredAbortControllerRef.current) {
          unfilteredAbortControllerRef.current.abort()
        }
        unfilteredAbortControllerRef.current = new AbortController()
        dispatch(fetchTicketCounts({ signal: unfilteredAbortControllerRef.current.signal }))

        if (mainAbortControllerRef.current) {
          mainAbortControllerRef.current.abort()
        }
        mainAbortControllerRef.current = new AbortController()

        dispatch(
          fetchAllTickets({
            status: filter === "all" ? undefined : filter,
            building: buildingFilter === "all" ? undefined : buildingFilter,
            signal: mainAbortControllerRef.current.signal,
          }),
        )
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${status === "closed" ? "close" : "reopen"} ticket`,
        variant: "destructive",
      })
    }
  }

  const handleDeleteTicket = async (ticketId: string) => {
    try {
      const result = await dispatch(deleteTicket(ticketId)).unwrap()
      if (result.success) {
        toast({
          title: "Success",
          description: "Ticket deleted successfully",
        })

        if (unfilteredAbortControllerRef.current) {
          unfilteredAbortControllerRef.current.abort()
        }
        unfilteredAbortControllerRef.current = new AbortController()
        dispatch(fetchTicketCounts({ signal: unfilteredAbortControllerRef.current.signal }))

        if (mainAbortControllerRef.current) {
          mainAbortControllerRef.current.abort()
        }
        mainAbortControllerRef.current = new AbortController()

        dispatch(
          fetchAllTickets({
            status: filter === "all" ? undefined : filter,
            building: buildingFilter === "all" ? undefined : buildingFilter,
            signal: mainAbortControllerRef.current.signal,
          }),
        )
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete ticket",
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, ticketId: "", ticketTitle: "" })
    }
  }

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter)
    updateFilters(newFilter, buildingFilter, searchTerm)
  }

  const handleBuildingFilterChange = (newBuildingFilter: string) => {
    setBuildingFilter(newBuildingFilter)
    updateFilters(filter, newBuildingFilter, searchTerm)
  }

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
    updateFilters(filter, buildingFilter, newSearchTerm)
  }

  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userFullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.roomNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const countForFilter = (counts?: { open: number; closed: number }) => {
    if (!counts) return 0
    if (filter === "open") return counts.open
    if (filter === "closed") return counts.closed
    return counts.open + counts.closed
  }

  const getBuildingTicketCount = (buildingId: string) => countForFilter(ticketCounts.byBuilding[buildingId])

  const getTotalTicketCount = () => countForFilter(ticketCounts.total)

  const getOpenTicketsCount = () => ticketCounts.total.open

  const getLastReplyStatus = (ticket: any) => {
    if (!ticket.comments || ticket.comments.length === 0) {
      return "noone"
    }

    const lastComment = ticket.comments[ticket.comments.length - 1]
    return lastComment.userRole === "admin" ? "admin" : "user"
  }

  // SoftOne send status per ticket (admin only); reloaded whenever the listed tickets change
  const [softoneRequests, setSoftoneRequests] = useState<Record<string, SoftoneRequestRecord>>({})
  const [sendingSoftoneTicketId, setSendingSoftoneTicketId] = useState<string | null>(null)
  const ticketIdsKey = tickets.map((ticket) => ticket.id).join(",")

  useEffect(() => {
    if (!ticketIdsKey) return
    let cancelled = false
    fetchSoftoneRequests(ticketIdsKey.split(","))
      .then(({ requests }) => {
        if (!cancelled) setSoftoneRequests(requests)
      })
      .catch((error) => console.error("Error loading SoftOne requests:", error))
    return () => {
      cancelled = true
    }
  }, [ticketIdsKey])

  const handleSendToSoftone = async (ticketId: string) => {
    setSendingSoftoneTicketId(ticketId)
    try {
      const request = await sendSoftoneRequest(ticketId)
      if (request) setSoftoneRequests((prev) => ({ ...prev, [ticketId]: request }))
      toast({
        title: request?.status === "sent" ? t("success") : t("error"),
        description:
          request?.status === "sent"
            ? t("softoneRequestSentToast")
            : request?.status === "waiting_link"
              ? t("softoneRequestWaitingHelp")
              : request?.error || t("softoneRequestSendError"),
        variant: request?.status === "sent" ? undefined : "destructive",
      })
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.response?.data?.error || t("softoneRequestSendError"),
        variant: "destructive",
      })
    } finally {
      setSendingSoftoneTicketId(null)
    }
  }

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="mb-4 d-flex justify-content-between align-items-center" style={{ height: "64px" }}>
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("ticketManagement")}</h1>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant={filter === "open" ? "primary" : "outline-primary"}
                onClick={() => handleFilterChange("open")}
                size="sm"
                className="px-3 py-1"
              >
                {t("openTickets")} ({getOpenTicketsCount()})
              </Button>
              <Button
                variant={filter === "closed" ? "primary" : "outline-primary"}
                onClick={() => handleFilterChange("closed")}
                size="sm"
                className="px-3 py-1"
              >
                {t("closedTickets")}
              </Button>
              <Button
                variant={filter === "all" ? "primary" : "outline-primary"}
                onClick={() => handleFilterChange("all")}
                size="sm"
                className="px-3 py-1"
              >
                {t("allTickets")}
              </Button>
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row gap-2 mb-3">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="building-filter" size="sm">
                <Filter size={16} className="me-1" />
                {buildingFilter === "all"
                  ? `${t("allBuildings")} (${getTotalTicketCount()})`
                  : `${buildings.find((b) => b.id === buildingFilter)?.name || buildingFilter} (${getBuildingTicketCount(buildingFilter)})`}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleBuildingFilterChange("all")}>
                  {t("allBuildings")} ({getTotalTicketCount()})
                </Dropdown.Item>
                {buildings.map((building) => (
                  <Dropdown.Item key={building.id} onClick={() => handleBuildingFilterChange(building.id)}>
                    {building.name} ({getBuildingTicketCount(building.id)})
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <div className="position-relative flex-grow-1">
              <Form.Control
                type="text"
                placeholder={t("searchTickets")}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="ps-5"
                size="lg"
              />
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
            </div>
          </div>

          {isLoading && tickets.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>{t("date")}</th>
                    <th>{t("building")}</th>
                    <th>{t("room")}</th>
                    <th>{t("user")}</th>
                    <th>{t("title")}</th>
                    <th>{t("type")}</th>
                    <th>{t("lastReply")}</th>
                    <th>{t("status")}</th>
                    <th>{t("softoneRequestColumn")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        {t("noTicketsFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        onClick={() =>
                          navigate(`/dashboard/tickets/${ticket.id}`, {
                            state: { from: `${location.pathname}${location.search}` },
                          })
                        }
                        style={{ cursor: "pointer" }}
                        className="table-row-hover"
                      >
                        <td className="align-middle">{formatDate(ticket.createdAt)}</td>
                        <td className="align-middle">
                          {buildings.find((b) => b.id === ticket.building)?.name || ticket.building}
                        </td>
                        <td className="align-middle">{ticket.roomNumber || "-"}</td>
                        <td className="align-middle">{ticket.userFullName}</td>
                        <td className="align-middle">{ticket.title}</td>
                        <td className="align-middle">{t(ticket.type)}</td>
                        <td className="align-middle">
                          <Badge
                            bg={
                              getLastReplyStatus(ticket) === "admin"
                                ? "danger"
                                : getLastReplyStatus(ticket) === "user"
                                  ? "primary"
                                  : "light"
                            }
                            text={getLastReplyStatus(ticket) === "noone" ? "dark" : undefined}
                          >
                            {t(getLastReplyStatus(ticket))}
                          </Badge>
                        </td>
                        <td className="align-middle">
                          <Badge bg={ticket.status === "open" ? "success" : "secondary"}>{t(ticket.status)}</Badge>
                        </td>
                        <td className="align-middle">
                          <SoftoneRequestBadge
                            request={softoneRequests[ticket.id]}
                            sending={sendingSoftoneTicketId === ticket.id}
                            onSend={() => handleSendToSoftone(ticket.id)}
                          />
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Link
                              to={`/dashboard/tickets/${ticket.id}`}
                              state={{ from: `${location.pathname}${location.search}` }}
                            >
                              <Button className="admin-edit-btn" size="sm" title={t("viewTicket")}>
                                {t("view")}
                              </Button>
                            </Link>
                            {ticket.status === "open" && (
                              <Button
                                className="admin-toggle-btn"
                                size="sm"
                                onClick={() => handleUpdateStatus(ticket.id, "closed")}
                                title={t("closeTicket")}
                              >
                                <XCircle size={16} />
                              </Button>
                            )}
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() =>
                                setDeleteConfirm({
                                  show: true,
                                  ticketId: ticket.id,
                                  ticketTitle: ticket.title,
                                })
                              }
                              title={t("deleteTicket")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, ticketId: "", ticketTitle: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteTicketConfirmation", { title: deleteConfirm.ticketTitle })}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, ticketId: "", ticketTitle: "" })}
          >
            {t("cancel")}
          </Button>
          <Button className="modal-delete-btn" onClick={() => handleDeleteTicket(deleteConfirm.ticketId)}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
